import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { 
  LayoutDashboard, Package, Heart, MessageCircle, Bot, BarChart3, 
  Settings, CreditCard, Plus, ChevronRight, Eye, TrendingUp, Menu, X,
  Bell, ShieldCheck, AlertTriangle, Store, ShoppingBag, ArrowRightLeft, FileText, CheckCircle2, Gavel, Dna, Truck, Egg, LifeBuoy
} from 'lucide-react';
import { api, formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';

export default function DashboardLayout() {
  const { user, isPremium, hasAI, activeMode, switchMode, isSeller, becomeSeller } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [notifications, setNotifications] = useState({ notifications: [], unread_count: 0 });
  const [billingStatus, setBillingStatus] = useState(null);
  const [sellerModalOpen, setSellerModalOpen] = useState(false);
  const [consentLiability, setConsentLiability] = useState(false);
  const [consentIntermediary, setConsentIntermediary] = useState(false);
  const [becomingLoading, setBecomingLoading] = useState(false);

  useEffect(() => {
    loadStats();
    loadNotifications();
    loadBilling();
  }, []);

  const loadStats = async () => {
    try {
      const data = await api.getDashboardStats();
      setStats(data);
    } catch (e) {
      console.error('Error loading stats:', e);
    }
  };

  const loadNotifications = async () => {
    try {
      const data = await api.getNotifications();
      setNotifications(data);
    } catch (e) {
      console.error('Error loading notifications:', e);
    }
  };

  const loadBilling = async () => {
    try {
      const data = await api.getBillingStatus();
      setBillingStatus(data);
    } catch (e) {
      console.error('Error loading billing:', e);
    }
  };

  const handleBecomeSeller = async () => {
    if (!isSeller) {
      setSellerModalOpen(true);
      return;
    }
  };

  const confirmBecomeSeller = async () => {
    if (!consentLiability || !consentIntermediary) {
      toast.error('Debes aceptar ambas declaraciones legales');
      return;
    }
    setBecomingLoading(true);
    try {
      await becomeSeller(consentLiability, consentIntermediary);
      toast.success('¡Ahora eres vendedor! Ya puedes crear anuncios.');
      setSellerModalOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBecomingLoading(false);
    }
  };

  const handleSwitchMode = async () => {
    const newMode = activeMode === 'seller' ? 'buyer' : 'seller';
    if (newMode === 'seller' && !isSeller) {
      handleBecomeSeller();
      return;
    }
    await switchMode(newMode);
    toast.success(newMode === 'seller' ? 'Modo vendedor activado' : 'Modo comprador activado');
  };

  const sellerNav = [
    { name: 'Resumen', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Mis anuncios', href: '/dashboard/anuncios', icon: Package },
    { name: 'Camadas', href: '/dashboard/camadas', icon: Egg },
    { name: 'Subastas', href: '/dashboard/subastas/nueva', icon: Gavel },
    { name: 'Compra Protegida', href: '/dashboard/pagos', icon: ShieldCheck },
    { name: 'Mensajes', href: '/dashboard/mensajes', icon: MessageCircle, badge: stats?.unread_messages },
    { name: 'Estadísticas', href: '/dashboard/estadisticas', icon: BarChart3 },
    { name: 'Recepcionista IA', href: '/dashboard/ia', icon: Bot, ai: true },
    { name: 'Suscripción', href: '/dashboard/suscripcion', icon: CreditCard },
    { name: 'Reputación', href: '/dashboard/reputacion', icon: ShieldCheck },
    { name: 'Soporte', href: '/dashboard/soporte', icon: LifeBuoy },
    { name: 'Configuración', href: '/dashboard/configuracion', icon: Settings },
  ];

  // Admin-only nav items
  const adminNav = user?.role === 'admin' ? [
    { name: 'Morphs Review', href: '/dashboard/admin/morphs', icon: Dna },
    { name: 'Enbixos', href: '/dashboard/enbixos', icon: Truck },
  ] : [];

  // Enbixos operator nav
  const operatorNav = user?.role === 'enbixos_operator' ? [
    { name: 'Enbixos', href: '/dashboard/enbixos', icon: Truck },
    { name: 'Configuración', href: '/dashboard/configuracion', icon: Settings },
  ] : [];

  const buyerNav = [
    { name: 'Resumen', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Favoritos', href: '/dashboard/favoritos', icon: Heart },
    { name: 'Subastas', href: '/subastas', icon: Gavel },
    { name: 'Compra Protegida', href: '/dashboard/pagos', icon: ShieldCheck },
    { name: 'Mensajes', href: '/dashboard/mensajes', icon: MessageCircle, badge: stats?.unread_messages },
    { name: 'Mis compras', href: '/dashboard/compras', icon: ShoppingBag },
    { name: 'Reputación', href: '/dashboard/reputacion', icon: ShieldCheck },
    { name: 'Soporte', href: '/dashboard/soporte', icon: LifeBuoy },
    { name: 'Configuración', href: '/dashboard/configuracion', icon: Settings },
  ];

  const navigation = user?.role === 'enbixos_operator' ? operatorNav : [...(activeMode === 'seller' ? sellerNav : buyerNav), ...adminNav];

  return (
    <div className="min-h-screen bg-[#0A0A0C]" data-testid="dashboard">
      {/* Mobile sidebar toggle */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-20 left-4 z-50 p-2 rounded-lg bg-zinc-800 text-zinc-400"
        data-testid="sidebar-toggle"
      >
        {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Sidebar */}
      <aside className={`fixed top-16 left-0 bottom-0 w-64 border-r border-white/5 bg-[#0A0A0C] z-40 transform transition-transform lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="p-6">
          {/* User info + badges */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center overflow-hidden" data-testid="sidebar-avatar">
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
              ) : (
                user?.name?.charAt(0).toUpperCase()
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="font-medium text-zinc-100 truncate">{user?.name}</p>
                {user?.badges?.verified_breeder && (
                  <ShieldCheck className="w-4 h-4 text-emerald-400 flex-shrink-0" title="Criador Verificado" />
                )}
              </div>
              <p className="text-xs text-zinc-500 capitalize">{user?.subscription || 'free'}</p>
            </div>
          </div>

          {/* Mode Switch */}
          <button
            onClick={handleSwitchMode}
            className="w-full flex items-center gap-2 px-3 py-2 mb-6 rounded-lg bg-zinc-800/50 border border-white/5 text-sm text-zinc-300 hover:bg-zinc-800 transition-colors"
            data-testid="mode-switch-btn"
          >
            <ArrowRightLeft className="w-4 h-4 text-zinc-500" />
            <span className="flex-1 text-left">
              {activeMode === 'seller' ? 'Modo Vendedor' : 'Modo Comprador'}
            </span>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-700 text-zinc-400">
              Cambiar
            </span>
          </button>

          {/* Become seller CTA */}
          {!isSeller && (
            <button
              onClick={handleBecomeSeller}
              className="w-full flex items-center gap-2 px-3 py-2.5 mb-6 rounded-lg bg-gradient-to-r from-emerald-600/20 to-teal-600/20 border border-emerald-500/20 text-emerald-400 text-sm hover:from-emerald-600/30 hover:to-teal-600/30 transition-all"
              data-testid="become-seller-btn"
            >
              <Store className="w-4 h-4" />
              <span>Conviértete en vendedor</span>
            </button>
          )}

          <nav className="space-y-1">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              const isDisabled = (item.premium && !isPremium) || (item.ai && !hasAI);
              
              return (
                <Link
                  key={item.name}
                  to={isDisabled ? '/dashboard/suscripcion' : item.href}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    isActive 
                      ? 'bg-white/5 text-zinc-100 border-l-2 border-purple-500' 
                      : isDisabled
                        ? 'text-zinc-600 cursor-not-allowed'
                        : 'text-zinc-400 hover:text-zinc-100 hover:bg-white/5'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="flex-1">{item.name}</span>
                  {item.badge > 0 && (
                    <span className="px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400 text-xs">
                      {item.badge}
                    </span>
                  )}
                  {(item.premium && !isPremium) && (
                    <span className="px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-400 text-[10px] font-bold">
                      PRO
                    </span>
                  )}
                  {(item.ai && !hasAI) && (
                    <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 text-[10px] font-bold">
                      IA
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Quick action */}
        {activeMode === 'seller' && isSeller && (
          <div className="absolute bottom-6 left-6 right-6">
            <Link to="/dashboard/anuncios/nuevo">
              <Button className="w-full bg-zinc-100 text-zinc-900 hover:bg-white" data-testid="new-listing-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo anuncio
              </Button>
            </Link>
          </div>
        )}
      </aside>

      {/* Main content */}
      <main className="lg:ml-64 pt-16 min-h-screen">
        <div className="p-6 md:p-8">
          {/* Billing Warning Banner */}
          {billingStatus?.is_expired && (
            <div className="mb-6 p-4 rounded-xl bg-red-600/10 border border-red-500/20 flex items-start gap-3" data-testid="billing-expired-banner">
              <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-300">Tu plan expiró</p>
                <p className="text-xs text-red-400/80 mt-0.5">
                  Renueva para reactivar tus anuncios y herramientas.
                  {billingStatus.paused_listings > 0 && ` ${billingStatus.paused_listings} anuncio(s) pausado(s).`}
                </p>
              </div>
              <Link to="/dashboard/suscripcion">
                <Button size="sm" className="bg-red-500 hover:bg-red-600 text-white text-xs">
                  Renovar
                </Button>
              </Link>
            </div>
          )}

          {billingStatus?.cancel_at_period_end && !billingStatus?.is_expired && (
            <div className="mb-6 p-4 rounded-xl bg-amber-600/10 border border-amber-500/20 flex items-start gap-3" data-testid="billing-cancelling-banner">
              <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-300">Cancelación programada</p>
                <p className="text-xs text-amber-400/80 mt-0.5">
                  Tu plan se cancelará al final del periodo. Tus beneficios seguirán activos hasta entonces.
                </p>
              </div>
              <Link to="/dashboard/suscripcion">
                <Button size="sm" variant="outline" className="border-amber-500/30 text-amber-400 text-xs">
                  Reactivar
                </Button>
              </Link>
            </div>
          )}

          {/* AI Activation Banner */}
          {hasAI && user?.ai_features?.enabled && !billingStatus?.is_expired && (
            <div className="mb-6 p-4 rounded-xl bg-emerald-600/5 border border-emerald-500/15 flex items-start gap-3" data-testid="ai-active-banner">
              <Bot className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-emerald-300">Tu Recepcionista IA ya esta activa y lista para atender compradores</p>
                <p className="text-xs text-emerald-400/60 mt-0.5">
                  Nivel: {user.ai_features.tier === 'lite' ? 'IA Lite' : user.ai_features.tier === 'full' ? 'IA Completa' : user.ai_features.tier === 'advanced' ? 'IA Avanzada' : 'N/A'}
                </p>
              </div>
              <Link to="/dashboard/ia">
                <Button size="sm" className="bg-emerald-600/20 text-emerald-400 border border-emerald-500/20 text-xs hover:bg-emerald-600/30">
                  Configurar
                </Button>
              </Link>
            </div>
          )}

          {/* AI Deactivation CTA */}
          {!hasAI && user?.ai_features?.deactivated_at && (
            <div className="mb-6 p-4 rounded-xl bg-zinc-800/50 border border-white/5 flex items-start gap-3" data-testid="ai-deactivated-banner">
              <Bot className="w-5 h-5 text-zinc-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-zinc-300">Renueva para reactivar tu Recepcionista IA</p>
                <p className="text-xs text-zinc-500 mt-0.5">Tu configuracion y FAQs estan guardadas. Al renovar se restauran sin perder contexto.</p>
              </div>
              <Link to="/precios">
                <Button size="sm" className="bg-purple-600 hover:bg-purple-500 text-white text-xs">
                  Renovar plan
                </Button>
              </Link>
            </div>
          )}

          <Outlet context={{ stats, loadStats, notifications, billingStatus, loadNotifications }} />
        </div>
      </main>

      {/* Seller Onboarding Modal with Legal Consent */}
      <Dialog open={sellerModalOpen} onOpenChange={setSellerModalOpen}>
        <DialogContent className="bg-[#121215] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="seller-onboarding-modal">
          <DialogHeader>
            <DialogTitle className="text-zinc-100 flex items-center gap-2">
              <Store className="w-5 h-5 text-emerald-400" />
              Conviértete en vendedor
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Para registrarte como vendedor en MorphMX, debes aceptar las siguientes declaraciones legales.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Consent 1: Seller Liability */}
            <div className="p-4 rounded-lg bg-zinc-800/50 border border-white/5">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consentLiability}
                  onChange={(e) => setConsentLiability(e.target.checked)}
                  className="mt-1 rounded border-white/20 bg-white/5 text-emerald-600 flex-shrink-0"
                  data-testid="consent-liability-checkbox"
                />
                <p className="text-xs text-zinc-400 leading-relaxed">
                  Declaro bajo protesta de decir verdad que toda publicación, ejemplar, documento, certificado,
                  permiso, factura, pedigree, carta de cesión o evidencia subida a MorphMX es auténtica, legal
                  y cumple con la legislación mexicana aplicable. Reconozco que soy el único responsable legal,
                  civil, administrativa, fiscal y penalmente por cualquier incumplimiento, falsedad documental,
                  posesión ilegal, comercialización restringida o uso ilícito de la plataforma derivado de mis
                  publicaciones o transacciones.
                </p>
              </label>
              {consentLiability && (
                <div className="mt-2 flex items-center gap-1.5 text-emerald-400 ml-7">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span className="text-xs">Aceptado</span>
                </div>
              )}
            </div>

            {/* Consent 2: Platform Intermediary */}
            <div className="p-4 rounded-lg bg-zinc-800/50 border border-white/5">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consentIntermediary}
                  onChange={(e) => setConsentIntermediary(e.target.checked)}
                  className="mt-1 rounded border-white/20 bg-white/5 text-emerald-600 flex-shrink-0"
                  data-testid="consent-intermediary-checkbox"
                />
                <p className="text-xs text-zinc-400 leading-relaxed">
                  Acepto y reconozco que MorphMX opera únicamente como plataforma intermediaria tecnológica
                  entre usuarios y no actúa como vendedor directo, propietario del ejemplar, importador ni
                  garante de la legalidad de los bienes ofertados por terceros. Asimismo, acepto que cualquier
                  uso indebido, fraudulento o ilícito de la plataforma será de mi exclusiva responsabilidad y
                  faculta a MorphMX para suspender mi cuenta, congelar operaciones, conservar evidencia y
                  cooperar con autoridades.
                </p>
              </label>
              {consentIntermediary && (
                <div className="mt-2 flex items-center gap-1.5 text-emerald-400 ml-7">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span className="text-xs">Aceptado</span>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setSellerModalOpen(false)} className="border-white/10 text-zinc-400">
                Cancelar
              </Button>
              <Button
                onClick={confirmBecomeSeller}
                disabled={!consentLiability || !consentIntermediary || becomingLoading}
                className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40"
                data-testid="confirm-become-seller-btn"
              >
                {becomingLoading ? 'Registrando...' : 'Confirmar y ser vendedor'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function DashboardHome() {
  const { user, isPremium, hasAI, activeMode, isSeller } = useAuth();
  const [stats, setStats] = useState(null);
  const [listings, setListings] = useState([]);
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [demoCount, setDemoCount] = useState(0);
  const [cleaningDemos, setCleaningDemos] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const promises = [api.getDashboardStats()];
      if (isSeller) {
        promises.push(api.getMyListings());
      }
      if (user?.id) {
        promises.push(api.getUserBadges(user.id));
      }

      const results = await Promise.all(promises);
      setStats(results[0]);
      if (isSeller && results[1]) setListings(results[1].slice(0, 5));
      if (results[results.length - 1]) setBadges(results[results.length - 1]);
      if (user?.role === 'admin') {
        try { const d = await api.getDemoCount(); setDemoCount(d.count); } catch (e) { console.error('Demo count error:', e); }
      }
    } catch (e) {
      console.error('Error:', e);
    } finally {
      setLoading(false);
    }
  };

  const sellerStatCards = [
    { label: 'Anuncios activos', value: stats?.active_listings || 0, icon: Package, color: 'purple' },
    { label: 'Vistas totales', value: stats?.total_views || 0, icon: Eye, color: 'blue' },
    { label: 'Favoritos', value: stats?.total_favorites || 0, icon: Heart, color: 'pink' },
    { label: 'Mensajes sin leer', value: stats?.unread_messages || 0, icon: MessageCircle, color: 'emerald' },
  ];

  const buyerStatCards = [
    { label: 'Favoritos', value: stats?.total_favorites || 0, icon: Heart, color: 'pink' },
    { label: 'Mensajes sin leer', value: stats?.unread_messages || 0, icon: MessageCircle, color: 'emerald' },
  ];

  const statCards = activeMode === 'seller' ? sellerStatCards : buyerStatCards;

  return (
    <div data-testid="dashboard-home">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <h1 className="text-2xl md:text-3xl font-light tracking-tight text-zinc-100 font-[Outfit]">
            Hola, {user?.name}
          </h1>
          {badges.map(b => (
            <span key={b.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs" title={b.tooltip}>
              <ShieldCheck className="w-3 h-3" />
              {b.label}
            </span>
          ))}
        </div>
        <p className="text-zinc-500">
          {activeMode === 'seller' ? 'Panel de vendedor' : 'Panel de comprador'}
        </p>
      </div>

      {/* Premium banner for sellers without AI */}
      {activeMode === 'seller' && !hasAI && isSeller && (
        <div className="mb-8 p-6 rounded-2xl bg-gradient-to-r from-purple-600/20 to-blue-600/20 border border-purple-500/20">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-medium text-zinc-100 mb-1">Activa tu Recepcionista IA</h3>
              <p className="text-sm text-zinc-400">Automatiza tu atención al cliente y vende más con el plan Basic</p>
            </div>
            <Link to="/dashboard/suscripcion">
              <Button className="bg-zinc-100 text-zinc-900 hover:bg-white">
                Ver planes
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className={`grid ${activeMode === 'seller' ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-2'} gap-4 mb-8`}>
        {statCards.map((stat) => (
          <div key={stat.label} className="glass-card p-5">
            <div className={`w-10 h-10 rounded-xl bg-${stat.color}-500/10 flex items-center justify-center mb-3`}>
              <stat.icon className={`w-5 h-5 text-${stat.color}-400`} />
            </div>
            <p className="text-2xl font-semibold text-zinc-100 font-[Outfit]">{stat.value}</p>
            <p className="text-sm text-zinc-500">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Recent Listings (seller mode) */}
      {activeMode === 'seller' && isSeller && (
        <div className="glass-card">
          <div className="flex items-center justify-between p-6 border-b border-white/5">
            <h2 className="text-lg font-medium text-zinc-100">Anuncios recientes</h2>
            <Link to="/dashboard/anuncios" className="text-sm text-purple-400 hover:text-purple-300">
              Ver todos
            </Link>
          </div>
          {loading ? (
            <div className="p-6 space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-16 skeleton rounded-lg" />
              ))}
            </div>
          ) : listings.length > 0 ? (
            <div className="divide-y divide-white/5">
              {listings.map((listing) => (
                <Link 
                  key={listing.id}
                  to={`/anuncio/${listing.id}`}
                  className="flex items-center gap-4 p-4 hover:bg-white/5 transition-colors"
                >
                  <div className="w-12 h-12 rounded-lg bg-zinc-800 overflow-hidden flex-shrink-0">
                    {listing.images?.[0] && (
                      <img src={listing.images[0]} alt="" className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-zinc-100 truncate">{listing.title}</p>
                    <div className="flex items-center gap-2">
                      <p className="text-xs text-zinc-500">{listing.category_name}</p>
                      {listing.status === 'paused_billing' && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400">Pausado</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-zinc-100">
                      ${listing.price?.toLocaleString()}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-zinc-500">
                      <span className="flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        {listing.views || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <Heart className="w-3 h-3" />
                        {listing.favorites_count || 0}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <p className="text-zinc-500 mb-4">Aún no tienes anuncios</p>
              <Link to="/dashboard/anuncios/nuevo">
                <Button className="bg-zinc-100 text-zinc-900 hover:bg-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Crear primer anuncio
                </Button>
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Buyer mode: Show explore CTA */}
      {activeMode === 'buyer' && (
        <div className="glass-card p-8 text-center">
          <h2 className="text-lg font-medium text-zinc-100 mb-2">Explorar animales disponibles</h2>
          <p className="text-sm text-zinc-500 mb-4">Encuentra tu próxima mascota o compañero de cría</p>
          <Link to="/explorar">
            <Button className="bg-zinc-100 text-zinc-900 hover:bg-white">
              Explorar marketplace
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      )}

      {/* Admin: Demo Cleanup */}
      {user?.role === 'admin' && demoCount > 0 && (
        <div className="glass-card p-6 mt-8 border border-amber-500/10" data-testid="admin-demo-cleanup">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-medium text-zinc-100">Publicaciones demo</h3>
              <p className="text-xs text-zinc-500 mt-0.5">{demoCount} publicaciones marcadas como demo en el marketplace</p>
            </div>
            <Button
              variant="outline"
              className="border-red-500/20 text-red-400 hover:bg-red-500/10 text-xs"
              disabled={cleaningDemos}
              onClick={async () => {
                if (!window.confirm(`¿Eliminar ${demoCount} publicaciones demo? Esta acción no se puede deshacer.`)) return;
                setCleaningDemos(true);
                try {
                  const res = await api.cleanupDemos();
                  toast.success(res.message);
                  setDemoCount(0);
                } catch (e) { toast.error(formatApiError(e)); }
                finally { setCleaningDemos(false); }
              }}
              data-testid="cleanup-demos-btn"
            >
              {cleaningDemos ? 'Limpiando...' : 'Limpiar publicaciones demo'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
